import { useMemo } from "react";

import type { DiscoveryReadinessContract } from "../types/discoveryReadiness";

import { resolveReadinessFixRoute } from "./readinessFixRoutes";

interface NextBestActionPanelProps {
  projectId: string;
  readiness: DiscoveryReadinessContract | null;
}

interface ActionSuggestion {
  label: string;
  reason: string;
  href?: string;
  indicator?: string;
}

function selectPrimaryAction(projectId: string, readiness: DiscoveryReadinessContract | null): ActionSuggestion {
  const failingRules = (readiness?.explain?.rules ?? []).filter((rule) => rule && !rule.passed);
  if (failingRules.length) {
    const sorted = [...failingRules].sort((a, b) => (b?.weight ?? 0) - (a?.weight ?? 0));
    const top = sorted[0];
    return {
      label: top?.ruleId ? `Fix ${top.ruleId}` : "Resolve highest-weight blocker",
      reason: "Addresses the most impactful readiness rule that is currently failing.",
      href: resolveReadinessFixRoute(projectId, top?.ruleId ?? "") ?? undefined,
      indicator: top?.weight != null ? `${Math.round((top.weight ?? 0) * 100)} weight` : undefined,
    };
  }

  const warnSignal = (readiness?.signals ?? []).find((signal) =>
    typeof signal?.severity === "string" && (signal.severity === "warn" || signal.severity === "error" || signal.severity === "critical"),
  );

  if (warnSignal) {
    return {
      label: warnSignal.title || "Resolve warning signal",
      reason: warnSignal.message || "Tackle the first warning surfaced by readiness signals.",
      href: resolveReadinessFixRoute(projectId, warnSignal?.evidence?.sourceType ?? "") ?? undefined,
      indicator: warnSignal.severity?.toString().toUpperCase(),
    };
  }

  return {
    label: "Review readiness",
    reason: "Validate discovery gates and confirm rules are passing.",
    href: `/projects/${encodeURIComponent(projectId)}?tab=coverage`,
  };
}

function buildSecondaryActions(projectId: string, readiness: DiscoveryReadinessContract | null): ActionSuggestion[] {
  const actions: ActionSuggestion[] = [];
  const failing = (readiness?.explain?.rules ?? []).filter((rule) => !rule.passed);

  failing.slice(0, 3).forEach((rule) => {
    actions.push({
      label: `Follow up on ${rule.ruleId}`,
      reason: "Secondary readiness fix to clear additional blockers.",
      href: resolveReadinessFixRoute(projectId, rule.ruleId) ?? undefined,
    });
  });

  if (!actions.length) {
    const firstSignal = (readiness?.signals ?? [])[0];
    if (firstSignal) {
      actions.push({
        label: firstSignal.title,
        reason: firstSignal.message,
        href: `/projects/${encodeURIComponent(projectId)}?tab=coverage`,
      });
    }
  }

  return actions;
}

export function NextBestActionPanel({ projectId, readiness }: NextBestActionPanelProps) {
  const progress = useMemo(() => {
    const total = readiness?.explain?.rules?.length ?? 0;
    const passed = readiness?.explain?.rules?.filter((rule) => rule.passed).length ?? 0;
    return { passed, total, percent: total ? Math.round((passed / total) * 100) : 0 };
  }, [readiness?.explain?.rules]);

  const primaryAction = useMemo(() => selectPrimaryAction(projectId, readiness), [projectId, readiness]);
  const secondaryActions = useMemo(() => buildSecondaryActions(projectId, readiness), [projectId, readiness]);

  return (
    <div className="hud-card hud-card--glow sticky top-4">
      <div className="hud-card__header">
        <div className="hud-label">Next best action</div>
        <div className="hud-chip hud-chip--accent">
          {progress.total ? `${progress.passed}/${progress.total} rules` : "Rules pending"}
        </div>
      </div>

      <div className="hud-card__body hud-card__body--stack">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="hud-title">Do this next</div>
            <div className="hud-value">{primaryAction.label}</div>
            <div className="hud-subtle">{primaryAction.reason}</div>
          </div>
          {primaryAction.indicator ? <div className="hud-chip">{primaryAction.indicator}</div> : null}
        </div>

        {primaryAction.href ? (
          <a className="button button-primary button-sm" href={primaryAction.href}>
            Go to fix
          </a>
        ) : null}

        <div className="hud-progress">
          <div className="hud-progress__bar">
            <span style={{ width: `${progress.percent}%` }} />
          </div>
          <div className="hud-progress__meta">
            <span>
              Passed {progress.passed} / {progress.total || "—"}
            </span>
            <span>{progress.percent ? `${progress.percent}%` : "0%"}</span>
          </div>
        </div>

        {secondaryActions.length ? (
          <div className="hud-card__section">
            <div className="hud-label">Secondary actions</div>
            <ul className="hud-list">
              {secondaryActions.map((action) => (
                <li key={`${action.label}-${action.href ?? "none"}`}>
                  {action.href ? (
                    <a className="hud-link" href={action.href}>
                      {action.label}
                    </a>
                  ) : (
                    <span className="hud-value--muted">{action.label}</span>
                  )}
                  <div className="hud-subtle">{action.reason}</div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="hud-footnote">
          Generated {readiness?.generatedAt ? new Date(readiness.generatedAt).toLocaleString() : "—"} •
          Defaults {readiness?.defaultsVersion ?? "—"}
        </div>
      </div>
    </div>
  );
}
